/**
 * 画面のテーマ(2026-09-22 発注者依頼「現行・A・B・E の4案を実システムで比較し、設定から切り替えたい」。D-132)。
 *
 *  - current = 改修前の見た目(既定)。tokens.css の値そのまま
 *  - a = スタンダードモダン / b = ダークモダン / e = プレミアムネイビー。値は src/styles/themes.css
 *  - 正式な設定は **ユーザーごと・ブラウザごと**(localStorage のキーに uid を入れる)。Firebase には保存しない
 *  - 画面を開いた瞬間の色のちらつきを防ぐため、最後にログインしていた人のテーマを別のキー(boot)にも持つ。
 *    index.html の最初のスクリプトがこれを読んで <html data-theme> を付ける。ログアウトしたら消す(共有PCで次の人に混ざらない)
 *
 * ここは画面を持たない純粋な関数だけ(テストあり: tests/theme.test.ts)。
 */

export const THEME_IDS = ['current', 'a', 'b', 'e'] as const;
export type ThemeId = (typeof THEME_IDS)[number];

export const DEFAULT_THEME: ThemeId = 'current';

export type ThemeMeta = {
  /** 画面に出す名前 */
  label: string;
  /** 切り替えボタンなどの短い名前 */
  short: string;
  /** 設定画面の説明 */
  description: string;
  /** 暗い背景のテーマか(ネイティブの入力欄・スクロールバーの色に使う) */
  dark: boolean;
};

export const THEMES: Record<ThemeId, ThemeMeta> = {
  current: { label: '現行', short: '現行', description: '改修前の見た目。明るい背景・ネイビーのサイドバー(既定)', dark: false },
  a: { label: 'A スタンダードモダン', short: 'A スタンダード', description: '白い面＋明るいグレーの背景＋ネイビーのサイドバー。余白を広げたシャープでクリーンな見た目', dark: false },
  b: { label: 'B ダークモダン', short: 'B ダーク', description: '暗いネイビーの背景に明るい文字。表・グラフ・入力欄も暗い背景用に調整', dark: true },
  e: { label: 'E プレミアムネイビー', short: 'E プレミアム', description: '深いネイビーの背景に白い情報カード。見出しと枠にアクセントの青', dark: false },
};

/** 正しいテーマIDだけを通す。それ以外(未設定・古い値・書き換えられた値)は現行 */
export function normalizeTheme(value: unknown): ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value) ? (value as ThemeId) : DEFAULT_THEME;
}

/** 正しいテーマIDか(URL の ?theme= など、無ければ使わないとき) */
export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

// ---- 保存(localStorage) -------------------------------------------------------------

/** 読み書きに使う最小限の形(テストでは Map で代用する) */
export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** ユーザーごとの正式なテーマ */
export const userThemeKey = (uid: string): string => `cc.theme.user.${uid}`;
/** 開いた瞬間に使うテーマ(最後にログインしていた人のもの)。index.html と同じキー */
export const BOOT_THEME_KEY = 'cc.theme.boot';

/** 保存できない環境(プライベートモードの一部など)でも画面は動かす */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function readUserTheme(storage: ThemeStorage, uid: string): ThemeId {
  return safe(() => normalizeTheme(storage.getItem(userThemeKey(uid))), DEFAULT_THEME);
}

export function writeUserTheme(storage: ThemeStorage, uid: string, theme: ThemeId): void {
  safe(() => storage.setItem(userThemeKey(uid), normalizeTheme(theme)), undefined);
}

export type BootTheme = { uid: string; theme: ThemeId };

export function readBootTheme(storage: ThemeStorage): BootTheme | null {
  return safe(() => {
    const raw = storage.getItem(BOOT_THEME_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { uid?: unknown; theme?: unknown };
    return typeof v.uid === 'string' && v.uid ? { uid: v.uid, theme: normalizeTheme(v.theme) } : null;
  }, null);
}

export function writeBootTheme(storage: ThemeStorage, uid: string, theme: ThemeId): void {
  safe(() => storage.setItem(BOOT_THEME_KEY, JSON.stringify({ uid, theme: normalizeTheme(theme) })), undefined);
}

export function clearBootTheme(storage: ThemeStorage): void {
  safe(() => storage.removeItem(BOOT_THEME_KEY), undefined);
}

/**
 * ログインの状態から、正式に使うテーマを決める。
 *  - 判定中(loading)は、開いた瞬間のテーマ(boot)をそのまま使う(ちらつかせない)
 *  - ログインしていない・使えない状態(招待なし・無効)は現行
 *  - ログインしていれば、その人の保存したテーマ(無ければ現行)
 */
export function resolveOfficialTheme(args: { status: 'loading' | 'signed-out' | 'no-profile' | 'inactive' | 'ready' | 'error'; uid: string | null; storage: ThemeStorage }): ThemeId {
  const { status, uid, storage } = args;
  // 権限の読込に失敗(error)した間もログインは続いているので、判定中と同じく開いた瞬間のテーマのままにする
  if (status === 'loading' || status === 'error') return readBootTheme(storage)?.theme ?? DEFAULT_THEME;
  if (status !== 'ready' || !uid) return DEFAULT_THEME;
  return readUserTheme(storage, uid);
}
